import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./OrderPages.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import AutoRefreshBar from "./AutoRefreshBar";
import { useSyncedOrderPagesAutoRefresh } from "./orderPagesAutoRefresh";
import usePageTitle from "./usePageTitle";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

function MainProcessIndexPage() {
  usePageTitle("Main Process Logs");
  const [processes, setProcesses] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useSyncedOrderPagesAutoRefresh();
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);
  const isMountedRef = useRef(true);

  const fetchIntervalMs = useMemo(() => {
    const env = process.env.REACT_APP_FETCH_INTERVAL_MS;
    const parsed = Number(env);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60000;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchProcesses = useCallback(async () => {
    try {
      const response = await auth.fetchWithAuth("/logs_rest/");
      if (response.status === 404) {
        if (isMountedRef.current) {
          setProcesses([]);
          setError("Per-order file logging is disabled (set WORKER_LOG_TO_FILE=1).");
        }
        return;
      }
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      if (isMountedRef.current) {
        setProcesses(Array.isArray(payload?.processes) ? payload.processes : []);
        setError("");
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [auth.fetchWithAuth]);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) return;
    let intervalId = null;
    fetchProcesses();
    if (autoRefresh) intervalId = setInterval(fetchProcesses, fetchIntervalMs);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [auth.authChecked, auth.isAuthenticated, autoRefresh, fetchIntervalMs, fetchProcesses]);

  return (
    <AuthGate auth={auth}>
      <div className="order-page">
        <div className="order-container">
          <h1 className="order-title">Main process logs (last ~48h)</h1>
          <AutoRefreshBar
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            fetchIntervalMs={fetchIntervalMs}
            onRefreshNow={fetchProcesses}
            subtitleWhenOn={`Lista odświeża się co ${Math.round(fetchIntervalMs / 1000)} s`}
            subtitleWhenOff="Odświeżanie co interwał jest wyłączone"
          />
          {error && <div className="order-error">{error}</div>}
          {loading ? (
            <div className="order-loader">
              <div className="order-spinner" />
              <p>Loading processes...</p>
            </div>
          ) : processes.length === 0 ? (
            <div>No main-process log entries yet.</div>
          ) : (
            <table className="order-table">
              <thead>
                <tr>
                  <th>Process (logger name)</th>
                  <th>Lines</th>
                  <th>First seen</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {processes.map((p) => (
                  <tr key={p.name}>
                    <td>
                      <a
                        href={`/logs_rest/${encodeURIComponent(p.name)}`}
                        className="order-link"
                      >
                        {p.name}
                      </a>
                    </td>
                    <td>{Number(p.line_count).toLocaleString()}</td>
                    <td>{formatDateTime(p.first_seen)}</td>
                    <td>{formatDateTime(p.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AuthGate>
  );
}

export default MainProcessIndexPage;
